import { useMemo } from 'react'
import { AdvisorData } from '../utils/types'
import { readLocalStorageJson } from '../utils/safeStorage'

/**
 * Checks whether stored ids exactly match the current advisor set.
 */
const storedIdsMatchCurrent = (storedIds: number[], advisorIds: number[]): boolean => {
  // If counts differ, we know the lists don’t match
  if (storedIds.length !== advisorIds.length) {
    return false
  }

  // Use a Set for quick lookups; this also handles duplicate IDs correctly
  const advisorIdSet = new Set(advisorIds)

  // Check each stored id exists in the current set
  return storedIds.every(id => advisorIdSet.has(id))
}

/**
 * Helper: fisher–yates shuffle on a copy.
 */
const shuffleAdvisors = (advisors: AdvisorData[]): AdvisorData[] => {
  const shuffled = [...advisors]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = shuffled[i]
    shuffled[i] = shuffled[j]
    shuffled[j] = temp
  }
  return shuffled
}

/**
 * Helper: persist ids to localStorage. A denied or full storage means the
 * order is simply not remembered; in a cross-site frame with third-party
 * storage blocked that is the normal state, so it is not reported.
 */
const persistOrder = (key: string, ids: number[]) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids))
  } catch {}
}

/**
 * Helper: read ids from localStorage
 */
const readPersistedOrder = (key: string): number[] | null => {
  const parsed = readLocalStorageJson(key)
  if (Array.isArray(parsed) && parsed.every(id => typeof id === 'number')) {
    return parsed
  }
  return null
}

const isSameOrder = (a: number[], b: number[]): boolean => {
  return a.length === b.length && a.every((id, index) => id === b[index])
}

const reorderByStoredIds = (advisors: AdvisorData[], storedIds: number[]): AdvisorData[] => {
  const advisorMap = new Map(advisors.map(advisor => [advisor.advisor.id, advisor]))
  return storedIds
    .map((id) => advisorMap.get(id))
    .filter((advisor): advisor is AdvisorData => Boolean(advisor))
}

const storageKeyPrefix = 'advisorMatchRandomOrder'

const getCookieValue = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Randomizes advisor order with persistence keyed by the funnel session cookie.
 */
export const useAdvisorOrderRandomization = (
  advisors: AdvisorData[] | undefined
): AdvisorData[] | undefined => {
  const sessionCookie = getCookieValue('saFunnelSessionUUID')
  const storageKey = `${storageKeyPrefix}:${sessionCookie || 'anon'}`

  return useMemo(() => {
    if (!advisors || advisors.length === 0) {
      return advisors
    }

    const advisorIds = advisors.map(advisor => advisor.advisor.id)
    const storedIds = readPersistedOrder(storageKey)
    if (storedIds && storedIdsMatchCurrent(storedIds, advisorIds)) {
      return reorderByStoredIds(advisors, storedIds)
    }

    // Swap instead of reshuffling — with few advisors, a reshuffle could return the same order repeatedly
    let shuffled = shuffleAdvisors(advisors)
    let shuffledIds = shuffled.map(advisor => advisor.advisor.id)
    if (isSameOrder(shuffledIds, advisorIds) && shuffled.length > 1) {
      const [first, second, ...rest] = shuffled
      shuffled = [second, first, ...rest]
      shuffledIds = shuffled.map(advisor => advisor.advisor.id)
    }
    persistOrder(storageKey, shuffledIds)
    return shuffled
  }, [advisors, storageKey])
}
